import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, Send, X, ThumbsUp, Clock, Car, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../services/api';

const RatingModal = ({ 
  ride, 
  booking, 
  type = 'ride', // 'ride' or 'booking'
  onClose, 
  onSubmitted 
}) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [categories, setCategories] = useState({
    punctuality: 0,
    friendliness: 0,
    quality: 0
  });
  const [submitting, setSubmitting] = useState(false);

  const item = ride || booking;
  const itemId = item?.id;
  const driverName = item?.driver_name || item?.worker_name || 'Service Provider';

  const categoryLabels = type === 'ride' 
    ? { punctuality: 'Punctuality', friendliness: 'Friendliness', quality: 'Driving' }
    : { punctuality: 'On Time', friendliness: 'Professionalism', quality: 'Quality of Work' };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      const endpoint = type === 'ride' 
        ? `/api/ratings/rides/${itemId}`
        : `/api/ratings/bookings/${itemId}`;
      
      await api.post(endpoint, {
        rating,
        comment: comment || null,
        categories: Object.values(categories).some(v => v > 0) ? categories : null
      });
      
      toast.success('Thank you for your feedback!');
      if (onSubmitted) onSubmitted();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit rating');
    }
    setSubmitting(false);
  };

  const StarRating = ({ value, onChange, onHover, size = 'lg' }) => {
    const sizeClass = size === 'lg' ? 'w-10 h-10' : 'w-6 h-6';
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => onHover && onHover(star)}
            onMouseLeave={() => onHover && onHover(0)}
            className="focus:outline-none transition-transform hover:scale-110"
          >
            <Star
              className={`${sizeClass} ${
                star <= (onHover ? hoveredRating || value : value)
                  ? 'fill-[#D4AF37] text-[#D4AF37]'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  const ratingLabels = {
    1: "Poor",
    2: "Fair",
    3: "Good",
    4: "Very Good",
    5: "Excellent"
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[var(--background-paper)] rounded-2xl max-w-md w-full overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 bg-gradient-to-r from-[#D4AF37]/20 to-[#F4C2C2]/20 border-b border-[var(--border-primary)]">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold">Rate Your {type === 'ride' ? 'Ride' : 'Service'}</h3>
              <p className="text-sm text-[var(--text-muted)]">
                How was your experience with {driverName}?
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Main Rating */}
          <div className="text-center">
            <p className="text-sm font-medium mb-3">Overall Rating</p>
            <div className="flex justify-center mb-2">
              <StarRating 
                value={rating} 
                onChange={setRating}
                onHover={setHoveredRating}
              />
            </div>
            <p className="text-sm text-[var(--text-muted)] h-5">
              {rating > 0 && ratingLabels[rating]}
            </p>
          </div>

          {/* Category Ratings */}
          <div className="space-y-3">
            <p className="text-sm font-medium">Rate Specific Areas (Optional)</p>
            {Object.entries(categoryLabels).map(([key, label]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm text-[var(--text-secondary)]">{label}</span>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setCategories(prev => ({ ...prev, [key]: star }))}
                      className="focus:outline-none"
                    >
                      <Star
                        className={`w-5 h-5 ${
                          star <= categories[key]
                            ? 'fill-[#D4AF37] text-[#D4AF37]'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Comment */}
          <div>
            <label className="text-sm font-medium block mb-2">
              <MessageSquare className="w-4 h-4 inline mr-1" />
              Add a Comment (Optional)
            </label>
            <textarea
              className="w-full p-3 border rounded-lg text-sm resize-none"
              rows="3"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={`Share your experience with ${driverName}...`}
              maxLength={500}
            />
            <p className="text-xs text-[var(--text-muted)] text-right mt-1">
              {comment.length}/500
            </p>
          </div>

          {/* Quick Tags */}
          <div>
            <p className="text-sm font-medium mb-2">Quick Feedback</p>
            <div className="flex flex-wrap gap-2">
              {[
                type === 'ride' ? 'Smooth ride' : 'Great work',
                'Very professional',
                'On time',
                'Friendly',
                'Would recommend'
              ].map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setComment(prev => prev ? `${prev} ${tag}.` : `${tag}.`)}
                  className="px-3 py-1 text-xs bg-[var(--background-subtle)] rounded-full hover:bg-[var(--primary)] hover:text-white transition-colors"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[var(--border-primary)] flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Skip
          </Button>
          <Button 
            className="flex-1" 
            onClick={handleSubmit}
            disabled={submitting || rating === 0}
          >
            {submitting ? 'Submitting...' : 'Submit Rating'}
            <Send className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default RatingModal;
